import { useEffect, useMemo, useState } from "react"

import { useMoney } from "../../../../../../modules/currency/useMoney"

import type { TakeawayCartItem, TakeawayKitchen, TakeawayProduct } from "../takeawayTypes"
import { calcItemsTotal } from "./takeawayCheckoutLib"
import { TakeawayBasketDrawerContent } from "./TakeawayBasketDrawerContent"
import { TakeawayCheckoutDialog } from "./TakeawayCheckoutDialog"

type TakeawayBasketDrawerProps = Readonly<{
    open: boolean
    cartByDate: Record<string, TakeawayCartItem[]>
    kitchen: TakeawayKitchen
    onQuantityChange: (product: TakeawayProduct, quantity: number, dateKey?: string) => void
    onClose: () => void
    onSuccess: () => void
}>

export function TakeawayBasketDrawer({
    open,
    cartByDate,
    kitchen,
    onQuantityChange,
    onClose,
    onSuccess,
}: TakeawayBasketDrawerProps) {
    const moneyFactory = useMoney({})
    const [showCheckout, setShowCheckout] = useState(false)

    const sortedDates = useMemo(() => Object.keys(cartByDate).sort(), [cartByDate])
    const allItems = useMemo(() => Object.values(cartByDate).flat(), [cartByDate])
    const totalPrice = useMemo(() => calcItemsTotal(allItems), [allItems])

    // Close basket when it becomes empty (matches mobile behavior)
    useEffect(() => {
        if (!allItems.length) onClose()
    }, [allItems.length, onClose])

    return (
        <>
            <TakeawayBasketDrawerContent
                open={open}
                sortedDates={sortedDates}
                cartByDate={cartByDate}
                kitchen={kitchen}
                moneyFactory={moneyFactory}
                totalPrice={totalPrice}
                isCheckoutOpen={showCheckout}
                canConfirm={allItems.length > 0}
                onQuantityChange={onQuantityChange}
                onClose={onClose}
                onConfirm={() => setShowCheckout(true)}
            />
            {showCheckout && (
                <TakeawayCheckoutDialog
                    cartByDate={cartByDate}
                    kitchen={kitchen}
                    onClose={() => setShowCheckout(false)}
                    onSuccess={() => {
                        setShowCheckout(false)
                        onSuccess()
                    }}
                    onCorrectOrder={() => setShowCheckout(false)}
                />
            )}
        </>
    )
}
